import React, { useState, useRef, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { useDrop } from "react-dnd";
import { API } from "aws-amplify";

import MagnetBoardSection from "./MagnetboardSection";
import Loader from "../../common/Loader";
import loading_gif from "../../../../images/balls.gif";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle, faUserGraduate } from "@fortawesome/free-solid-svg-icons";
import { mapCoursePeriodSectionsSortedByQuarter } from "../../../../js/Mappers";
import { getSchedulePeriodIdFromCoursePeriodId } from "../../../../js/PeriodHelpers";
import { getSectionAlignmentRenderItems } from "../../utils/sectionTermUtils";
import { getClassroomPeriodStudentCount } from "../utils/classroomOccupancy";

const EMPTY_PERIOD_RESTRICTIONS = [];

const MagnetboardDropPeriod = React.memo(({
  currentScheduleVersionID,
  currentView,
  currentViewType,
  currentDay,
  schoolInfo,
  scheduleInfo,
  scheduleType,
  scheduleStructureData,
  sections,
  setSections,
  subsections,
  labels,
  teachers,
  setTeachers,
  courses,
  classrooms,
  students,
  setStudents,
  classroomID,
  teacherID,
  courseID,
  studentID,
  coursePeriodID,
  periodID,
  periodName,
  setConflictsLoading,
  setMatchPercentLoading,
  setMatchPercent,
  labsOnlyFiltered,
  nonLabsOnlyFiltered,
  singletonsFiltered,
  doubletonsFiltered,
  noClassroomFiltered,
  hasClassroomFiltered,
  toggleEditSection,
  duplicateSectionFromMagnetboard,
  deleteSectionFromMagnetboard,
  setDuplicateLoadingTarget,
  deleteLoadingTargets,
  setDeleteLoadingTarget,
  handleSelectQuickViewDataID,
  selectedQuickViewSectionID,
  magnetboardOptions,
  updateSectionPlacement,
  updateManualPlacement,
  sectionIsAllowedForCurrentUser = () => true,
  toggleLockSection,
  dataSections,
  sectionAlignmentTemplate = null,
  isLunchPeriod,
  isRestrictedPeriod,
  togglePeriodAvailability = () => Promise.resolve(false),
  isDuplicateLoading = false,
  studentConflictSectionLookup,
  sectionConflictSummaryBySectionId,
  activeDragRestrictionInfo = null,
  onDropHoverChange = null,
}) => {
  /////////////////////
  ///// USE STATE /////
  /////////////////////
  const dataID = useRef(
    currentView === "classroom"
      ? classroomID
      : currentView === "course"
      ? courseID
      : teacherID
  );
  // const [sortedDataSections, setSortedDataSections] = useState([]);
  const [addNewSectionIsOpen, setAddNewSectionIsOpen] = useState(false);
  const [isDropLoading, setIsDropLoading] = useState(false);
  const [isAvailabilityLoading, setIsAvailabilityLoading] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState(null);

  /////////////////////////////
  ///// USE DROP FUNCTION /////
  /////////////////////////////
  const [{ isOver, canDrop, draggedItemType, draggedDataID, draggedCoursePeriodID, draggedTeacherRestrictedPeriods, draggedCourseRestrictedPeriods }, dropRef] = useDrop({
    accept: ["section", "manual-section", "lunch"],
    canDrop: (item) => {
      if (item.type !== "section" && item.type !== "manual-section") return true;
      const dragged_section = sections.find(section => `${section.section_id}` === `${item.sectionID}`);
      if (!sectionIsAllowedForCurrentUser(dragged_section)) return false;
      if (currentView === "classroom") return true;

      return `${item.draggedDataID || ""}` === `${dataID.current || ""}`;
    },
    drop: async (item) => {
      if (
        currentView !== "classroom" &&
        (item.type === "section" || item.type === "manual-section") &&
        `${item.draggedDataID || ""}` !== `${dataID.current || ""}`
      ) {
        return;
      }

      setIsDropLoading(true);
      await new Promise(resolve => setTimeout(resolve, 100));
      
      try {
        if (item.type === "section") {
          const dragged_section = sections.find(section => `${section.section_id}` === `${item.sectionID}`);
          if (!sectionIsAllowedForCurrentUser(dragged_section)) return;
          await updateSectionPlacement({
            ...item,
            droppedDataID: dataID.current,
            droppedCoursePeriodID: coursePeriodID,
          });
        } else if (item.type === "manual-section") {
          const dragged_section = sections.find(section => `${section.section_id}` === `${item.sectionID}`);
          if (!sectionIsAllowedForCurrentUser(dragged_section)) return;
          await updateManualPlacement({
            updateType: "add",
            sectionID: item.sectionID,
            droppedCoursePeriodID: coursePeriodID,
          });
        } else if (item.type === "lunch") {
          const new_schedule_period_id = getSchedulePeriodIdFromCoursePeriodId(
            scheduleStructureData,
            item.draggedCoursePeriodID,
            currentDay
          );
          if (!new_schedule_period_id) throw new Error("Unable to map lunch course period to schedule period");
          await updateLunchSchedule(
            currentView,
            new_schedule_period_id,
            dataID.current
          );
        }
      } catch (error) {
        console.error("DROP PERIOD ERROR:", error);
      } finally {
        setIsDropLoading(false);
      }
    },
    collect: (monitor) => {
      const item = monitor.getItem();
      const item_type = item?.type || null;
      const is_section_drag = item_type === "section" || item_type === "manual-section";
      const current_is_over = monitor.isOver();
      const dragged_data_id = item?.draggedDataID || null;
      const row_matches_drag_origin = `${dragged_data_id || ""}` === `${dataID.current || ""}`;
      const should_collect_drag_restrictions = is_section_drag && (
        currentView === "classroom" ? current_is_over : row_matches_drag_origin
      );

      return {
        isOver: current_is_over,
        canDrop: monitor.canDrop(),
        draggedItemType: should_collect_drag_restrictions ? item_type : null,
        draggedDataID: should_collect_drag_restrictions ? dragged_data_id : null,
        draggedCoursePeriodID: should_collect_drag_restrictions ? item?.draggedCoursePeriodID || null : null,
        draggedTeacherRestrictedPeriods: should_collect_drag_restrictions ? item?.teacherRestrictedPeriods || EMPTY_PERIOD_RESTRICTIONS : EMPTY_PERIOD_RESTRICTIONS,
        draggedCourseRestrictedPeriods: should_collect_drag_restrictions ? item?.courseRestrictedPeriods || EMPTY_PERIOD_RESTRICTIONS : EMPTY_PERIOD_RESTRICTIONS,
      };
    },
  });

  // ///////////////////
  // ///// USE REF /////
  // ///////////////////
  const add_new_section_ref = useRef();
  const context_menu_ref = useRef();

  useEffect(() => {
    if (!addNewSectionIsOpen) return;

    const handleOutsideClick = (e) => {
      if (
        add_new_section_ref.current &&
        !add_new_section_ref.current.contains(e.target)
      ) {
        setAddNewSectionIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [addNewSectionIsOpen]);

  useEffect(() => {
    if(!contextMenuPosition) return;

    const closeContextMenu = event => {
      if(context_menu_ref.current && context_menu_ref.current.contains(event.target)) return;
      setContextMenuPosition(null);
    };
    const closeContextMenuOnEscape = event => {
      if(event.key === 'Escape') setContextMenuPosition(null);
    };

    document.addEventListener('mousedown', closeContextMenu);
    document.addEventListener('contextmenu', closeContextMenu);
    document.addEventListener('keydown', closeContextMenuOnEscape);
    window.addEventListener('resize', closeContextMenu);
    window.addEventListener('scroll', closeContextMenu, true);

    return () => {
      document.removeEventListener('mousedown', closeContextMenu);
      document.removeEventListener('contextmenu', closeContextMenu);
      document.removeEventListener('keydown', closeContextMenuOnEscape);
      window.removeEventListener('resize', closeContextMenu);
      window.removeEventListener('scroll', closeContextMenu, true);
    };
  }, [contextMenuPosition]);

  useEffect(() => {
    if (typeof onDropHoverChange !== "function") return;

    if (!isOver || (draggedItemType !== "section" && draggedItemType !== "manual-section")) {
      onDropHoverChange(coursePeriodID, null);
      return;
    }

    onDropHoverChange(coursePeriodID, {
      draggedItemType,
      draggedDataID,
      draggedCoursePeriodID,
      draggedTeacherRestrictedPeriods,
      draggedCourseRestrictedPeriods,
    });

    return () => {
      onDropHoverChange(coursePeriodID, null);
    };
  }, [
    coursePeriodID,
    draggedCoursePeriodID,
    draggedCourseRestrictedPeriods,
    draggedDataID,
    draggedItemType,
    draggedTeacherRestrictedPeriods,
    isOver,
    onDropHoverChange,
  ]);

  // /////////////////////
  // ///// FUNCTIONS /////
  // /////////////////////
  const updateLunchSchedule = async (
    user_type,
    schedule_period_id,
    user_id
  ) => {
    const data = {
      school_id: schoolInfo.school_id,
      schedule_version_id: schoolInfo.current_schedule_version_id,
      user_type: user_type,
      user_id: user_id,
      schedule_period_id: schedule_period_id,
    };

    const apiName = process.env.REACT_APP_ENDPOINT_NAME;
    const url = "/update-lunch-schedule";
    const myInit = {
      // OPTIONAL
      response: true,
      body: JSON.stringify(data),
    };

    try {
      await API.post(apiName, url, myInit);

      if (user_type === "teacher") {
        const teacher_index = teachers.findIndex(
          (teacher) => teacher.teacher_id === user_id
        );
        if (teacher_index !== -1)
          teachers[teacher_index].lunch_schedule_period_id = schedule_period_id;

        setTeachers([...teachers]);
      } else if (user_type === "student") {
        const student_index = students.findIndex(
          (student) => student.student_id === user_id
        );
        if (student_index !== -1)
          students[student_index].lunch_schedule_period_id = schedule_period_id;

        setStudents([...students]);
      }
    } catch (e) {
      console.log(e.response);
    }
  };

  const subsectionsMap = useMemo(() => 
    subsections.reduce((map, sub) => {
      map[sub.subsection_id] = sub.section_id;
      return map;
    }, {}), [subsections]
  );

  const rowHasRestriction = isRestrictedPeriod;
  const canDragSections = magnetboardOptions.allow_section_dragging !== false;
  const canEditSectionStructure = magnetboardOptions.allow_section_structure_editing !== false;
  const canEditSectionRoster = magnetboardOptions.allow_section_roster_editing !== false;

  const dragRestrictionDetails = useMemo(() => {
    const emptyDetails = {
      hasTeacherRestriction: false,
      hasCourseRestriction: false,
    };

    const restrictionSource = currentView === "classroom" && activeDragRestrictionInfo
      ? activeDragRestrictionInfo
      : {
          draggedItemType,
          draggedDataID,
          draggedCoursePeriodID,
          draggedTeacherRestrictedPeriods,
          draggedCourseRestrictedPeriods,
        };
    const sourceItemType = restrictionSource.draggedItemType;
    const sourceDataID = restrictionSource.draggedDataID;
    const sourceCoursePeriodID = restrictionSource.draggedCoursePeriodID;

    if (sourceItemType !== "section" && sourceItemType !== "manual-section")
      return emptyDetails;
    if (`${sourceCoursePeriodID || ""}` === `${coursePeriodID || ""}`) {
      return emptyDetails;
    }
    if (
      currentView !== "classroom" &&
      `${sourceDataID || ""}` !== `${dataID.current || ""}`
    ) {
      return emptyDetails;
    }
    if (currentView === "classroom" && !activeDragRestrictionInfo) {
      return emptyDetails;
    }

    const teacherRestrictedPeriods = Array.isArray(restrictionSource.draggedTeacherRestrictedPeriods)
      ? restrictionSource.draggedTeacherRestrictedPeriods.map((period_id) => `${period_id}`)
      : [];
    const courseRestrictedPeriods = Array.isArray(restrictionSource.draggedCourseRestrictedPeriods)
      ? restrictionSource.draggedCourseRestrictedPeriods.map((period_id) => `${period_id}`)
      : [];

    return {
      hasTeacherRestriction:
        (currentView === "course" || currentView === "classroom") &&
        teacherRestrictedPeriods.includes(`${coursePeriodID}`),
      hasCourseRestriction:
        (currentView === "teacher" || currentView === "classroom") &&
        courseRestrictedPeriods.includes(`${coursePeriodID}`),
    };
  }, [
    activeDragRestrictionInfo,
    draggedItemType,
    draggedDataID,
    draggedCoursePeriodID,
    draggedTeacherRestrictedPeriods,
    draggedCourseRestrictedPeriods,
    coursePeriodID,
    currentView,
  ]);

  const effectiveIsRestrictedPeriod =
    rowHasRestriction ||
    dragRestrictionDetails.hasTeacherRestriction ||
    dragRestrictionDetails.hasCourseRestriction;

  const activeRestrictionItemType = currentView === "classroom" && activeDragRestrictionInfo
    ? activeDragRestrictionInfo.draggedItemType
    : draggedItemType;
  const hasDragRestriction =
    activeRestrictionItemType === "section" || activeRestrictionItemType === "manual-section";

  const dragRestrictionOverlayLines = useMemo(() => {
    if (!hasDragRestriction) return [];

    const lines = [];
    if (dragRestrictionDetails.hasTeacherRestriction) {
      lines.push({ label: "Teacher", title: "Teacher restriction" });
    }
    if (dragRestrictionDetails.hasCourseRestriction) {
      lines.push({ label: "Course", title: "Course restriction" });
    }
    return lines;
  }, [hasDragRestriction, dragRestrictionDetails]);

  const sortedDataSections = useMemo(() => {

    let logged_section_ids = new Set();
    let data_sections = dataSections.filter(sectionIsAllowedForCurrentUser).reduce((results, section) => {
      const has_section_id =
        section &&
        section.section_id !== undefined &&
        section.section_id !== null &&
        `${section.section_id}` !== "";

      if (!has_section_id) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("Skipping Magnetboard section without section_id", {
            currentView,
            dataID: dataID.current,
            coursePeriodID,
            section,
          });
        }
        return results;
      }

      const section_id = section.section_id;
      const main_section_id =  subsectionsMap[section_id] || section_id;

      if (logged_section_ids.has(main_section_id)) return results;
      logged_section_ids.add(main_section_id);

      const section_course_periods =
        "course_periods" in section ? section.course_periods : [];
      if (currentView === "student" || section_course_periods.includes(coursePeriodID))
        results.push(section);

      return results;
    }, []);
    return currentView === "student"
      ? data_sections
      : mapCoursePeriodSectionsSortedByQuarter(data_sections);
  }, [currentView, dataSections, coursePeriodID, subsectionsMap, sectionIsAllowedForCurrentUser]);

  const alignedSectionRenderItems = useMemo(
    () => getSectionAlignmentRenderItems(sortedDataSections, sectionAlignmentTemplate),
    [sectionAlignmentTemplate, sortedDataSections]
  );

  const classroomPeriodStudentCount = useMemo(() => {
    if (currentView !== "classroom") return 0;

    return getClassroomPeriodStudentCount({
      displaySections: sortedDataSections,
      allSections: sections,
      subsectionParentByID: subsectionsMap,
    });
  }, [currentView, sections, sortedDataSections, subsectionsMap]);

  const openSectionModalFromDropdown = (sectionType) => {
    setAddNewSectionIsOpen(false);
    setContextMenuPosition(null);
    toggleEditSection({
      section_id: null,
      data_id: dataID.current,
      section_type: sectionType,
      course_period_id: coursePeriodID,
    });
  };

  const runLunchFromDropdown = () => {
    setAddNewSectionIsOpen(false);
    updateLunchSchedule("teacher", periodID, dataID.current);
  };

  const canAddSectionFromContextMenu = currentView !== 'student' && magnetboardOptions.allow_new_sections === true;
  const canToggleAvailabilityFromContextMenu =
    ['teacher', 'course'].includes(currentView) &&
    magnetboardOptions.allow_period_availability_editing !== false;

  const openPeriodContextMenu = event => {
    if(!canAddSectionFromContextMenu && !canToggleAvailabilityFromContextMenu) return;
    if(event.target.closest('.mb-section, .mb-student-section, .mb-add-new-section')) return;

    event.preventDefault();
    event.stopPropagation();
    setAddNewSectionIsOpen(false);

    const menu_width = 240;
    const menu_height = canAddSectionFromContextMenu && canToggleAvailabilityFromContextMenu ? 94 : 48;
    setContextMenuPosition({
      left: Math.max(8, Math.min(event.clientX, window.innerWidth - menu_width - 8)),
      top: Math.max(8, Math.min(event.clientY, window.innerHeight - menu_height - 8)),
    });
  };

  const toggleAvailabilityFromContextMenu = async () => {
    setContextMenuPosition(null);
    setIsAvailabilityLoading(true);

    try
    {
      await togglePeriodAvailability({
        data_type: currentView,
        data_id: dataID.current,
        course_period_id: coursePeriodID,
        is_restricted: isRestrictedPeriod,
      });
    }
    finally
    {
      setIsAvailabilityLoading(false);
    }
  };

  return (
    <div
      className={`mb-inner-box mb-schedule-inner-box ${
        effectiveIsRestrictedPeriod ? "mb-schedule-inner-box-restricted" : ""
      } ${
        rowHasRestriction ? "mb-schedule-inner-box-row-restricted" : ""
      } ${
        isOver && canDrop ? "mb-schedule-inner-box-hovered" : ""
      }`}
      ref={dropRef}
      onClick={openPeriodContextMenu}
      style={{
        position: 'relative',
        minHeight: '60px',
      }}
    >
      {/* Dashed Line Drop Indicator or Loader */}
      {((isOver && canDrop) || isDropLoading || isAvailabilityLoading) && (
        <div className="mb-schedule-inner-box-dashed-line">
          {(isDropLoading || isAvailabilityLoading) ? (
            <Loader size="48px" />
          ) : null }
        </div>
      )}
      {rowHasRestriction && (
        <div className="mb-schedule-inner-box-restricted-label">
          Restricted
        </div>
      )}
      {dragRestrictionOverlayLines.length > 0 && (
        <div className={`mb-schedule-inner-box-restriction-overlay ${
          rowHasRestriction ? "mb-schedule-inner-box-restriction-overlay-with-row-label" : ""
        }`}>
          {dragRestrictionOverlayLines.map((line_info, index) => (
            <div
              className="mb-schedule-inner-box-restriction-pill"
              key={`${coursePeriodID}-restriction-line-${index}`}
              title={line_info.title}
            >
              {line_info.label}
            </div>
          ))}
        </div>
      )}
      {isLunchPeriod && currentView === "student" &&
        <div className='mb-student-section'>
          <div className='mb-section-top-bar pastel-yellow'>
            <div>Lunch</div>
          </div>
          <div className='mb-section-content'>
            <div className='mb-section-name ellipsis'>Lunch</div>
          </div>
        </div>
      }
      {currentView !== "student" && <div className="print-period-name">{periodName}</div>}
      {/* {isLunchPeriod && currentView === "teacher" &&
				<div className='mb-section mb-lunch-section' ref={lunchDragRef}>
					<div className='mb-section-top-bar pastel-yellow'>
						<div>Lunch</div>
						<div className='mb-section-more-info opaque-link' onClick={() => updateLunchSchedule('teacher', null, teacherID)}><FontAwesomeIcon icon={faTrashAlt}/></div>
					</div>
					<div className='mb-section-content'>
						<div className='mb-section-name ellipsis'>Lunch</div>
					</div>
				</div>
			} */}
      {alignedSectionRenderItems.map((render_item) => {
        if (render_item.type === "placeholder") {
          return (
            <div
              key={`${dataID.current || ""}-${coursePeriodID || ""}-${render_item.key}`}
              className="mb-section-alignment-placeholder"
              aria-hidden="true"
            />
          );
        }

        const { section_info } = render_item;
        const section_can_edit = sectionIsAllowedForCurrentUser(section_info);
        const can_open_section = section_can_edit && (
          currentView === "student" ? canEditSectionRoster : (canEditSectionStructure || canEditSectionRoster)
        );
        return (
          <MagnetBoardSection
            key={`${dataID.current || ""}-${coursePeriodID || ""}-${section_info.section_id}`}
            schoolInfo={schoolInfo}
            scheduleInfo={scheduleInfo}
            scheduleStructureData={scheduleStructureData}
            scheduleType={scheduleType}
            currentView={currentView}
            currentViewType={currentViewType}
            currentDay={currentDay}
            currentScheduleVersionID={currentScheduleVersionID}
            teachers={teachers}
            courses={courses}
            classrooms={classrooms}
            sections={sections}
            setSections={setSections}
            subsections={subsections}
            labels={labels}
            setConflictsLoading={setConflictsLoading}
            setMatchPercent={setMatchPercent}
            setMatchPercentLoading={setMatchPercentLoading}
            labsOnlyFiltered={labsOnlyFiltered}
            nonLabsOnlyFiltered={nonLabsOnlyFiltered}
            singletonsFiltered={singletonsFiltered}
            doubletonsFiltered={doubletonsFiltered}
            noClassroomFiltered={noClassroomFiltered}
            hasClassroomFiltered={hasClassroomFiltered}
            toggleEditSection={toggleEditSection}
            updateSectionPlacement={updateSectionPlacement}
            duplicateSectionFromMagnetboard={duplicateSectionFromMagnetboard}
            deleteSectionFromMagnetboard={deleteSectionFromMagnetboard}
            setDuplicateLoadingTarget={setDuplicateLoadingTarget}
            deleteLoadingTargets={deleteLoadingTargets}
            setDeleteLoadingTarget={setDeleteLoadingTarget}
            dataID={dataID.current}
            coursePeriodID={coursePeriodID}
            sectionID={section_info.section_id}
            studentID={studentID}
            sectionType="section"
            handleSelectQuickViewDataID={handleSelectQuickViewDataID}
            selectedQuickViewSectionID={selectedQuickViewSectionID}
            toggleLockSection={toggleLockSection}
            isDraggable={currentView !== "student" && canDragSections && section_can_edit && section_info.locked !== "1"}
            isEditable={can_open_section}
            isClassroomEditable={canEditSectionStructure && section_can_edit}
            isLockable={currentView !== "student" && canEditSectionStructure && section_can_edit}
            isQuickViewEnabled={currentView !== "student" && canEditSectionStructure}
            studentConflictSectionLookup={studentConflictSectionLookup}
            sectionConflictSummaryBySectionId={sectionConflictSummaryBySectionId}
            magnetboardOptions={magnetboardOptions}
          />
        );
      })}
      {isDuplicateLoading && (
        <div className="mb-section-action-placeholder">
          <img className="mb-section-balls-loader" src={loading_gif} alt="loading..." />
        </div>
      )}
      {magnetboardOptions.allow_new_sections && (
        <div className="mb-add-new-section" ref={add_new_section_ref}>
          <FontAwesomeIcon
            className="gray-to-blue-link"
            icon={faPlusCircle}
            onClick={
              magnetboardOptions.allow_placeholders
                ? () => setAddNewSectionIsOpen(!addNewSectionIsOpen)
                : () =>
                    toggleEditSection({
                      section_id: null,
                      data_id: dataID.current,
                      section_type: "1",
                      course_period_id: coursePeriodID,
                    })
            }
          />
          {dataID && addNewSectionIsOpen && (
            <div
              className="dropdown-options-container"
              style={{ top: "27px", zIndex: "999" }}
            >
              <div
                className="dropdown-option left-border-dark-blue-thick"
                onClick={() => openSectionModalFromDropdown("1")}
              >
                New Section
              </div>
              {currentView === "teacher" && (
                <>
                  {currentViewType === "day" && (
                    <div
                      className="dropdown-option left-border-pastel-yellow-thick"
                      onClick={runLunchFromDropdown}
                    >
                      Lunch
                    </div>
                  )}
                  <div
                    className="dropdown-option left-border-pastel-light-blue-thick"
                    onClick={() => openSectionModalFromDropdown("2")}
                  >
                    Prep
                  </div>
                  <div
                    className="dropdown-option left-border-pastel-brown-thick"
                    onClick={() => openSectionModalFromDropdown("3")}
                  >
                    Duty
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
      {currentView === "classroom" && classroomPeriodStudentCount > 0 && (
        <div
          className="mb-classroom-period-student-count"
          title={`${classroomPeriodStudentCount} student${classroomPeriodStudentCount === 1 ? "" : "s"} in this classroom during ${periodName}`}
          aria-label={`${classroomPeriodStudentCount} student${classroomPeriodStudentCount === 1 ? "" : "s"} in this classroom during ${periodName}`}
        >
          <FontAwesomeIcon icon={faUserGraduate} />
          <span>{classroomPeriodStudentCount}</span>
        </div>
      )}
      {contextMenuPosition && typeof document !== 'undefined' && createPortal(
        <div
          ref={context_menu_ref}
          className="mb-period-context-menu"
          style={{left:`${contextMenuPosition.left}px`, top:`${contextMenuPosition.top}px`}}
          role="menu"
          onClick={event => event.stopPropagation()}
        >
          {canAddSectionFromContextMenu && (
            <div
              className="dropdown-option left-border-dark-blue-thick"
              onClick={() => openSectionModalFromDropdown("1")}
              role="menuitem"
            >
              Add a Section
            </div>
          )}
          {canToggleAvailabilityFromContextMenu && (
            <div
              className={`dropdown-option ${isRestrictedPeriod ? 'left-border-green-thick' : 'left-border-red-thick'}`}
              onClick={toggleAvailabilityFromContextMenu}
              role="menuitem"
            >
              Make Period {isRestrictedPeriod ? 'Available' : 'Unavailable'}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
});

export default MagnetboardDropPeriod;
